// src/pages/auth/Login.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import SocialAuthButtons from '../../components/auth/SocialAuthButtons';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login = () => {
  const [formData, setFormData] = useState({
    email: localStorage.getItem('rememberedEmail') || '',
    password: ''
  });
  const [remember, setRemember] = useState(!!localStorage.getItem('rememberedEmail'));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, confirmTwoFA, applyAuth } = useAuth();
  // P3-A : 2e étape (code TOTP ou code de secours)
  const [twoFA, setTwoFA] = useState(null); // { tempToken }
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFABusy, setTwoFABusy] = useState(false);
  const finishLogin = (data) => {
    toast.success('Connexion réussie !');
    const role = data?.user?.role;
    // 'staff' doit aller au BACK-OFFICE, pas sur l'espace client. Sans cette
    // ligne, un commercial se connectait et atterrissait sur « Mon Compte » :
    // le role etait bien pose en base, mais rien ne l'y menait — on croyait que
    // le passage en personnel n'avait pas fonctionne.
    // Un membre d'equipe a le role 'user' : c'est `isTeamMember` qui le
    // distingue d'un simple client, sinon il atterrit sur « Mon Compte » alors
    // qu'il est bien Actif dans l'equipe d'une boutique.
    const isTeam = !!(data?.user?.isTeamMember);
    if (role === 'admin' || role === 'staff') navigate('/admin/dashboard');
    else if (role === 'merchant' || isTeam) navigate('/merchant/dashboard');
    else navigate('/dashboard');
  };
  const submitTwoFA = async (e) => {
    e.preventDefault();
    if (!twoFA || !twoFACode.trim()) return;
    setTwoFABusy(true);
    try {
      const data = await confirmTwoFA(twoFA.tempToken, twoFACode.trim());
      finishLogin(data);
    } catch (err) {
      toast.error(err?.message || 'Code incorrect');
    } finally { setTwoFABusy(false); }
  };

  const navigate = useNavigate();

  useEffect(() => {
    setErrors(prev => ({ ...prev, server: undefined }));
  }, [formData.email, formData.password]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Retour de connexion sociale réussie : un JWT est passé en query.
    const ssoToken = params.get('token');
    if (ssoToken) {
      applyAuth({ token: ssoToken });
      toast.success('Connexion réussie !');
      // On nettoie l'URL (retire le token de l'historique) et on redirige.
      navigate('/', { replace: true });
      return;
    }

    const em = params.get('email');
    if (em) setFormData((p) => ({ ...p, email: em }));
    const social = params.get('social');
    const provider = params.get('provider') || '';
    const cap = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'sociale';
    if (social === 'unavailable') {
      toast.info(`La connexion ${cap} n'est pas encore configurée. Utilisez votre e-mail pour le moment.`);
    } else if (social === 'pending') {
      toast.info(`Connexion ${cap} en cours de configuration.`);
    } else if (social === 'error') {
      toast.error(`Échec de la connexion ${cap}. Réessayez ou utilisez votre e-mail.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.email || !EMAIL_REGEX.test(formData.email)) {
      errs.email = 'Entrez une adresse email valide';
    }
    if (!formData.password || formData.password.length < 6) {
      errs.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    if (remember) {
      localStorage.setItem('rememberedEmail', formData.email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    try {
      const data = await login(formData.email, formData.password);
      if (data?.requiresTwoFA) { setTwoFA({ tempToken: data.tempToken }); return; }
      toast.success('Connexion réussie !');

      const role = data?.user?.role || data?.role || (data?.data && data.data.user && data.data.user.role);

      const isTeam = !!(data?.user?.isTeamMember || data?.isTeamMember);

      if (role === 'admin' || role === 'staff') {
        navigate('/admin/dashboard');
      } else if (role === 'merchant' || isTeam) {
        navigate('/merchant/dashboard');
      } else {
        navigate('/dashboard/profile'); // ✅ Vers profil
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur de connexion — vérifiez vos identifiants';
      setErrors(prev => ({ ...prev, server: msg }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // P3-A : écran de saisie du code 2FA
  if (twoFA) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
        <form onSubmit={submitTwoFA} className="bg-white rounded-2xl shadow p-8 w-full max-w-sm text-center">
          <span className="mx-auto w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4 text-2xl font-bold">2FA</span>
          <h1 className="text-xl font-bold mb-1">Vérification en deux étapes</h1>
          <p className="text-sm text-gray-500 mb-5">Entrez le code à 6 chiffres de votre application d'authentification, ou un code de secours.</p>
          <input autoFocus value={twoFACode} onChange={(e) => setTwoFACode(e.target.value)}
            placeholder="123 456" inputMode="numeric"
            className="w-full text-center text-2xl font-mono tracking-[0.4em] rounded-xl border border-gray-200 px-3 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-primary-200" />
          <button type="submit" disabled={twoFABusy || !twoFACode.trim()}
            className="w-full px-4 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-50">
            {twoFABusy ? 'Vérification...' : 'Valider'}
          </button>
          <button type="button" onClick={() => { setTwoFA(null); setTwoFACode(''); }}
            className="mt-3 text-sm text-gray-500 hover:underline">Retour à la connexion</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h2 className="text-3xl font-bold text-center mb-6">Connexion</h2>

        <SocialAuthButtons />
        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-500">Ou avec un e-mail</span>
          <span className="flex-1 h-px bg-gray-200" />
        </div>

        {errors.server && (
          <div role="alert" aria-live="assertive" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 p-3 rounded">
            {errors.server}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              autoFocus
              required
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
            />
            {errors.email && <p id="email-error" className="mt-2 text-xs text-red-600">{errors.email}</p>}
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 px-2 py-1"
              >
                {showPassword ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {errors.password && <p id="password-error" className="mt-2 text-xs text-red-600">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between mb-6">
            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Se souvenir de moi</span>
            </label>

            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 text-sm">
              Mot de passe oublié ?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center"
            aria-busy={loading}
          >
            {loading ? <span className="inline-block">Connexion…</span> : 'Se connecter'}
          </button>
        </form>

        <div className="mt-6 text-center text-gray-600">
          Pas encore de compte ?{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-700 font-semibold">
            S'inscrire
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;